import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { Form } from 'react-bootstrap';

interface NumberInputProps {
  value: number | string | null | undefined;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number | 'any';
  size?: 'sm' | 'lg';
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  style?: React.CSSProperties;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

/**
 * Controlled numeric <Form.Control> that behaves like a normal text field while typing:
 * backspace/clear/retype work immediately, because the displayed text is only clamped
 * back to a valid number on blur — never mid-edit. A plain controlled number input that
 * coerces on every keystroke (e.g. `onChange={(e) => setQty(parseInt(e.target.value) || 1)}`)
 * snaps back to the fallback the instant the field is cleared, which makes backspacing to
 * retype feel broken and forces users to select-all before typing a new value.
 *
 * Spinner arrows default to whole-unit steps (step=1) even for otherwise-float values, so
 * e.g. a quantity of 1.00 becomes 2.00 on one click instead of 1.01 — typing an exact
 * decimal by hand still works normally.
 */
export default function NumberInput({ value, onChange, min, max, step = 1, onFocus, onBlur, ...rest }: NumberInputProps) {
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(value === null || value === undefined ? '' : String(value));
  }, [value]);

  const clamp = (n: number) => {
    let result = n;
    if (min !== undefined && min !== null) result = Math.max(min, result);
    if (max !== undefined && max !== null) result = Math.min(max, result);
    return result;
  };

  return (
    <Form.Control
      type="number"
      step={step}
      min={min}
      max={max}
      value={text}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        focused.current = true;
        onFocus?.(event);
      }}
      onChange={(event) => {
        const raw = (event.target as HTMLInputElement).value;
        setText(raw);
        const parsed = parseFloat(raw);
        if (!Number.isNaN(parsed)) onChange(parsed);
      }}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        focused.current = false;
        const parsed = parseFloat(text);
        const next = clamp(Number.isNaN(parsed) ? (min ?? 0) : parsed);
        setText(String(next));
        onChange(next);
        onBlur?.(event);
      }}
      {...rest}
    />
  );
}
